import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Lightbulb,
  Stethoscope,
  Tags,
  Bell,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
}

const WriterSidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: '/writer/dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
    },
    {
      title: 'Notifications',
      path: '/writer/notifications',
      icon: <Bell className="w-5 h-5" />,
    },
    {
      title: 'Health Blogs',
      path: '/writer/blogs',
      icon: <FileText className="w-5 h-5" />,
    },
    {
      title: 'Doctor Blogs',
      path: '/writer/doctor-blogs',
      icon: <Stethoscope className="w-5 h-5" />,
    },
    {
      title: 'Doctor Blog Categories',
      path: '/writer/doctor-blog-categories',
      icon: <Tags className="w-5 h-5" />,
    },
    {
      title: 'Health Awareness',
      path: '/writer/awareness',
      icon: <Lightbulb className="w-5 h-5" />,
    },

  ];

  return (
    <div
      className={`h-screen bg-white dark:bg-gray-800 transition-all duration-300 ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        {!collapsed && (
          <div className="font-semibold text-lg text-primary-600 dark:text-primary-400">Vedika Writer</div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          ) : (
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          )}
        </button>
      </div>

      <div className="py-4">
        <nav>
          <ul>
            {menuItems.map((item) => {
              // Avoid `/writer/blogs` matching `/writer/blogs/...` while also
              // treating `/writer/doctor-blogs` as its own section.
              const isActive =
                location.pathname === item.path ||
                (item.path !== '/writer/blogs' &&
                  location.pathname.startsWith(`${item.path}/`)) ||
                (item.path === '/writer/blogs' &&
                  location.pathname.startsWith('/writer/blogs/'));

              return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 ${
                    collapsed ? 'justify-center' : 'space-x-3'
                  } ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-r-4 border-primary-600 dark:border-primary-400'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span>{item.icon}</span>
                  {!collapsed && <span>{item.title}</span>}
                </Link>
              </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="absolute bottom-0 w-full p-4">
        <button
          onClick={handleLogout}
          className={`flex items-center text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 ${
            collapsed ? 'justify-center w-full' : 'space-x-2'
          }`}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default WriterSidebar;

